import bancolombia from '../assets/bancolombia.png';
import nequi from '../assets/nequi.png';
import pse from '../assets/pse-logo.png';
import creditCard from '../assets/credit-card.png';
import breb from '../assets/breb.png';
import { date, now } from '../common/time';
import { isNumber } from 'lodash';

type PaymentMethodConfig = {
  active: boolean;
  showWhenInactive?: boolean;
  daysBeforeExpiration?: number;
  daysBeforeEndOfMonth?: number;
  daysAfterDueDate?: number;
};

type Merchant = {
  paymentMethods: {
    pse: PaymentMethodConfig;
    nequiButton: PaymentMethodConfig;
    bancolombiaButton: PaymentMethodConfig;
    card: PaymentMethodConfig;
    breb: PaymentMethodConfig;
    cash: PaymentMethodConfig;
  };
  gateway?: boolean;
  bancolombiaGateway?: boolean;
  bancolombiaButtonPreferred?: boolean;
};

type Invoice = {
  dueDate?: string;
  expirationDate?: string;
  cardPaymentActive?: boolean;
  amount: number;
};

const paymentMethods = [
  { id: 'pse', paymentMethod: 'PSE', imageUrl: pse, maxAmount: 550_000_000 },
  { id: 'card', paymentMethod: 'Tarjetas de Crédito', imageUrl: creditCard, maxAmount: 99_000_000 },
  { id: 'bancolombiaButton', paymentMethod: 'Botón Bancolombia', imageUrl: bancolombia, maxAmount: 15_000_000 },
  { id: 'nequiButton', paymentMethod: 'Botón Nequi', imageUrl: nequi, maxAmount: 400_000_000 },
  { id: 'breb', paymentMethod: 'Bre-B', imageUrl: breb, maxAmount: 11_552_000 },
  { id: 'cash', paymentMethod: 'Pago en corresponsal Bancolombia', imageUrl: bancolombia, maxAmount: 15_000_000 },
] as const;

export const getVisiblePaymentMethodsForInvoice = (
  merchant: Merchant | undefined,
  invoices: Invoice[],
  totalAmountToPay: number,
  hasSubscriptionAmount: boolean = false
) => {
  if (!merchant) return [];

  const availableMethods = paymentMethods
    .map((method) => {
      const config = merchant.paymentMethods[method.id];
      const expirationThreshold = now().add(config?.daysBeforeExpiration ?? 0, 'days');
      const endOfMonthThreshold = now().add(config?.daysBeforeEndOfMonth ?? 0, 'days');
      const afterDueDateThreshold = now().subtract(config?.daysAfterDueDate ?? 0, 'days');

      // Check if method meets activation criteria for all invoices
      const meetsActivationCriteria = invoices.every((invoice) => {
        const isExpiringSoon = expirationThreshold > date(invoice.expirationDate);
        const isEndOfMonthSoon = endOfMonthThreshold > now().endOf('month');
        const isAfterDueDate = isNumber(config?.daysAfterDueDate) && afterDueDateThreshold > date(invoice.dueDate);
        const isCardEnabled = method.id === 'card' && invoice.cardPaymentActive;
        return isExpiringSoon || isEndOfMonthSoon || isAfterDueDate || isCardEnabled;
      });
      const isActive = config?.active || meetsActivationCriteria;
      const exceedsMaxAmount = method.maxAmount < totalAmountToPay;
      const blockedBySubscription =
        hasSubscriptionAmount && method.id === 'bancolombiaButton' && !!merchant.bancolombiaGateway;
      const shouldHide = (!isActive && !config?.showWhenInactive) || exceedsMaxAmount || blockedBySubscription;

      return { ...method, active: isActive, hidden: shouldHide, blockedBySubscription };
    })
    .filter((method) => !method.hidden);

  // When the merchant prefers Botón Bancolombia, surface it first.
  if (merchant.bancolombiaButtonPreferred) {
    return [...availableMethods].sort((a, b) => {
      if (a.id === b.id) return 0;
      if (a.id === 'bancolombiaButton') return -1;
      if (b.id === 'bancolombiaButton') return 1;
      return 0;
    });
  }

  return availableMethods;
};
